import btRemoteImg from "@/assets/accessories/bt-remote-tv6-ultra.jpg";
import remoteV3Img from "@/assets/accessories/remote-v3.jpg";
import remoteV21Img from "@/assets/accessories/remote-v2.1.jpg";
import remoteV2Img from "@/assets/accessories/remote-v2.jpg";
import airRemoteImg from "@/assets/accessories/air-remote.jpg";
import hdmiCableImg from "@/assets/accessories/hdmi-cable.jpg";
import remoteStandardImg from "@/assets/accessories/remote-standard.jpg";
import avCableImg from "@/assets/accessories/av-cable.jpg";
import powerSupplyTv5Img from "@/assets/accessories/power-supply-tv5.jpg";
import powerSupplyStandardImg from "@/assets/accessories/power-supply-standard.jpg";
import irSensorImg from "@/assets/accessories/ir-sensor.jpg";
import type { Locale } from "@/lib/site";

export type AccessoryCategory = "remotes" | "cables" | "power" | "sensors";

export type Accessory = {
  id: string;
  category: AccessoryCategory;
  image: string;
  descriptionLink?: {
    accessoryId: string;
    text: string;
  };

  en: {
    title: string;
    shortDesc: string;
    description: string;
    keyFeatures?: string[];
    specifications?: Record<string, string>;
  };
  localized?: Partial<
    Record<
      Locale,
      {
        title?: string;
        shortDesc?: string;
        description?: string;
        keyFeatures?: string[];
        specifications?: Record<string, string>;
      }
    >
  >;
};

export const accessories: Accessory[] = [
  {
    id: "bt-remote-tv6-ultra",
    category: "remotes",
    image: btRemoteImg,
    en: {
      title: "inext BT Remote for TV6 Ultra",
      shortDesc:
        "A convenient Bluetooth remote for Android TV and media players. Supports voice search via a built-in microphone. Compact, lightweight, and easy to use — the perfect solution for watching content and navigating apps.",
      description:
        "The inext BT Remote is the official Bluetooth remote control for the inext TV6 Ultra TV box, designed to provide a comfortable, intuitive way to control your device and access your favorite content instantly. Thanks to its wireless Bluetooth connection, it works without requiring direct line-of-sight with the device — making your experience smoother and more convenient.",
      keyFeatures: [
        "Bluetooth connection — operate the remote from any angle in the room, no need to point it directly at the TV box.",
        "Programmable button (white) — customize it to fit your personal needs.",
        "Quick-access YouTube button — launch your favorite service with a single press.",
        "Easy control — all essential buttons for navigation, volume, playback, and settings right at your fingertips.",
        "Ergonomic design — compact and lightweight, fits comfortably in your hand.",
        "Voice control — use the built-in microphone to search for content by voice (YouTube, Google Play, browser, and more).",
      ],
      specifications: {
        "Connection type": "Bluetooth",
        Compatibility: "inext TV6 Ultra",
        "Programmable button": "Yes (white)",
        Purpose: "For TV boxes and TVs with inext devices",
        Color: "Black",
        "Body material": "Plastic",
      },
    },
    localized: {
      uk: {
        title: "Пульт inext BT Remote для TV6 Ultra",
        shortDesc:
          "Зручний Bluetooth-пульт для Android TV та медіаплеєрів. Підтримує голосовий пошук через вбудований мікрофон. Компактний, легкий та простий у використанні — ідеальне рішення для перегляду контенту та навігації в додатках.",
        description:
          "Офіційний пульт Bluetooth для приставки inext TV6 Ultra, створений для забезпечення комфортного та інтуїтивно зрозумілого керування пристроєм та миттєвого доступу до улюбленого контенту. Завдяки бездротовому з'єднанню Bluetooth він працює без необхідності прямої видимості приставки — що робить користування плавнішим та зручнішим.",
        keyFeatures: [
          "З'єднання Bluetooth — керуйте пристроєм під будь-яким кутом у кімнаті, не потрібно направляти пульт на приставку.",
          "Програмована кнопка (біла) — налаштуйте її під власні потреби.",
          "Кнопка швидкого доступу до YouTube — запускайте улюблений сервіс одним натисканням.",
          "Просте керування — всі основні кнопки для навігації, гучності, відтворення та налаштувань під рукою.",
          "Ергономічний дизайн — компактний і легкий пульт, який зручно лежить у руці.",
          "Голосове керування — використовуйте вбудований мікрофон для пошуку контенту голосом (YouTube, Google Play, браузер тощо).",
        ],
        specifications: {
          "Тип підключення": "Bluetooth",
          Сумісність: "inext TV6 Ultra",
          "Програмована кнопка": "Так (біла)",
          Призначення: "Для медіаплеєрів та телевізорів із пристроями inext",
          Колір: "Чорний",
          "Матеріал корпусу": "Пластик",
        },
      },
      pl: {
        title: "Pilot inext BT Remote do TV6 Ultra",
        shortDesc:
          "Wygodny pilot Bluetooth do urządzeń z Android TV i odtwarzaczy multimedialnych. Wbudowany mikrofon umożliwia wyszukiwanie głosowe. Pilot jest kompaktowy, lekki i łatwy w obsłudze.",
        description:
          "inext BT Remote to oryginalny pilot Bluetooth do modelu inext TV6 Ultra. Zapewnia wygodne sterowanie urządzeniem i szybki dostęp do najczęściej używanych funkcji. Łączność Bluetooth oznacza, że pilota nie trzeba kierować w stronę dekodera.",
        keyFeatures: [
          "Połączenie Bluetooth — pilot działa pod dowolnym kątem w pomieszczeniu, bez potrzeby celowania w dekoder.",
          "Programowalny przycisk (biały) — możliwość dostosowania do własnych potrzeb.",
          "Przycisk szybkiego dostępu do YouTube — uruchomienie ulubionej aplikacji jednym naciśnięciem.",
          "Wygodne sterowanie — wszystkie niezbędne przyciski do nawigacji, regulacji głośności, odtwarzania i ustawień.",
          "Ergonomiczna konstrukcja — kompaktowy i lekki pilot, który wygodnie leży w dłoni.",
          "Sterowanie głosowe — dzięki wbudowanemu mikrofonowi można wyszukiwać treści głosem (YouTube, Google Play, przeglądarka itp.).",
        ],
        specifications: {
          "Rodzaj połączenia": "Bluetooth",
          Kompatybilność: "inext TV6 Ultra",
          "Programowalny przycisk": "tak (biały)",
          Przeznaczenie: "do przystawek TV i telewizorów z urządzeniami inext",
          Kolor: "czarny",
          "Materiał obudowy": "plastik",
        },
      },
      pt: {
        title: "Controle inext BT Remote para TV6 Ultra",
        shortDesc:
          "Um controle remoto Bluetooth prático para Android TV e media players. Suporta busca por voz através de um microfone integrado. Compacto, leve e fácil de usar — a solução ideal para assistir a conteúdos e navegar pelos aplicativos.",
        description:
          "O inext BT Remote é o controle remoto Bluetooth oficial da set-top box inext TV6 Ultra, desenvolvido para oferecer uma forma confortável e intuitiva de controlar o seu aparelho e acessar instantaneamente os seus conteúdos favoritos. Graças à conexão sem fio Bluetooth, ele funciona sem precisar apontar diretamente para o aparelho — tornando a sua experiência mais fluida e prática.",
        keyFeatures: [
          "Conexão Bluetooth — opere o controle de qualquer ângulo do ambiente, sem precisar apontá-lo diretamente para a set-top box.",
          "Botão programável (branco) — personalize-o de acordo com as suas necessidades.",
          "Botão de acesso rápido ao YouTube — abra o seu serviço favorito com um único toque.",
          "Controle simples — todos os botões essenciais para navegação, volume, reprodução e configurações ao alcance dos seus dedos.",
          "Design ergonômico — compacto e leve, encaixa-se confortavelmente na mão.",
          "Controle por voz — use o microfone integrado para buscar conteúdos por voz (YouTube, Google Play, navegador e muito mais).",
        ],
        specifications: {
          "Tipo de conexão": "Bluetooth",
          Compatibilidade: "inext TV6 Ultra",
          "Botão programável": "Sim (branco)",
          Finalidade: "Para set-top boxes e TVs com aparelhos inext",
          Cor: "Preto",
          "Material do corpo": "Plástico",
        },
      },
      es: {
        title: "Control inext BT Remote para TV6 Ultra",
        shortDesc:
          "Un práctico control remoto Bluetooth para Android TV y reproductores multimedia. Admite la búsqueda por voz mediante un micrófono integrado. Compacto, ligero y fácil de usar — la solución ideal para ver contenido y navegar por las apps.",
        description:
          "El inext BT Remote es el control remoto Bluetooth oficial de la Set-Top Box inext TV6 Ultra, diseñado para ofrecer una forma cómoda e intuitiva de controlar el dispositivo y acceder al instante a sus contenidos favoritos. Gracias a la conexión inalámbrica Bluetooth, funciona sin necesidad de apuntar directamente al dispositivo — lo que hace su experiencia más fluida y práctica.",
        keyFeatures: [
          "Conexión Bluetooth — opera el control desde cualquier ángulo del ambiente, sin necesidad de apuntarlo directamente a la Set-Top Box.",
          "Botón programable (blanco) — personalícelo según sus necesidades.",
          "Botón de acceso rápido a YouTube — abra su servicio favorito con un solo toque.",
          "Control sencillo — todos los botones esenciales para navegación, volumen, reproducción y ajustes al alcance de sus dedos.",
          "Diseño ergonómico — compacto y ligero, se adapta cómodamente a la mano.",
          "Control por voz — use el micrófono integrado para buscar contenido por voz (YouTube, Google Play, navegador y mucho más).",
        ],
        specifications: {
          "Tipo de conexión": "Bluetooth",
          Compatibilidad: "inext TV6 Ultra",
          "Botón programable": "Sí (blanco)",
          Finalidad: "Para Set-Top Boxes y TVs con dispositivos inext",
          Color: "Negro",
          "Material del cuerpo": "Plástico",
        },
      },
    },
  },
  {
    id: "remote-v3",
    category: "remotes",
    image: remoteV3Img,
    en: {
      title: "Remote control inext V3.0",
      shortDesc:
        "Universal remote control for inext set-top boxes with a programming zone. Compatible with set-top box models – TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2.",
      description:
        "The inext programmable remote control allows you to simultaneously control your set-top box and TV.",
      keyFeatures: [
        "Programmable TV Control Zone — learn commands from your television remote control to simplify your setup.",
        "Numeric keypad — easily switch channels directly on your IPTV service.",
        "Compatible with all classic inext models — TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2.",
        "Full navigation and media playback control buttons.",
      ],
      specifications: {
        Type: "Universal Programmable IR Remote",
        Compatibility: "inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2",
        "Programmable Keys": "Yes (TV Power, TV Input, Vol+, Vol-)",
        "Power source": "2 x AAA Batteries (not included)",
        Color: "Black",
      },
    },
    localized: {
      uk: {
        title: "Пульт дистанційного керування inext V3.0",
        shortDesc:
          "Універсальний пульт дистанційного керування для приставок inext із зоною програмування. Сумісний з моделями приставок – TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2.",
        description:
          "Програмований пульт inext дозволяє одночасно керувати приставкою та телевізором.",
        keyFeatures: [
          "Програмована зона керування ТВ — навчіть пульт основним командам вашого телевізора (живлення, гучність, джерело сигналу).",
          "Цифрова клавіатура — зручне перемикання каналів на ТБ та в IPTV додатках.",
          "Сумісність — працює з більшістю моделей приставок inext (TV5, TV5 Ultra, TV4, 4K Ultra тощо).",
          "Повний набір кнопок навігації та керування відтворенням мультимедіа.",
        ],
        specifications: {
          Тип: "Універсальний програмований ІЧ-пульт",
          Сумісність: "inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2",
          "Програмовані клавіші": "Так (Ввімкнення ТВ, Вибір джерела, Гучність+, Гучність-)",
          Живлення: "2 x AAA батарейки (не входять до комплекту)",
          Колір: "Чорний",
        },
      },
      pl: {
        title: "Pilot zdalnego sterowania inext V3.0",
        shortDesc:
          "Uniwersalny, programowalny pilot do dekoderów inext. Jest zgodny z modelami TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3 i 4K2.",
        description:
          "Programowalny pilot zdalnego sterowania inext umożliwia jednoczesne sterowanie dekoderem i telewizorem.",
        keyFeatures: [
          "Programowalna strefa TV — pozwala skopiować najważniejsze funkcje pilota telewizora i obsługiwać oba urządzenia jednym pilotem.",
          "Klawiatura numeryczna — prosta i szybka zmiana kanałów bezpośrednio w serwisie IPTV.",
          "Szeroka kompatybilność — działa ze standardowymi modelami dekoderów inext.",
          "Wygodny blok przycisków nawigacyjnych i sterowania multimediami.",
        ],
        specifications: {
          Typ: "Uniwersalny programowalny pilot IR",
          Kompatybilność: "inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2",
          "Programowalne klawisze": "tak (zasilanie TV, wybór źródła, głośność +/−)",
          Zasilanie: "2 × baterie AAA (brak w zestawie)",
          Kolor: "czarny",
        },
      },
      pt: {
        title: "Controle remoto inext V3.0",
        shortDesc:
          "Controle remoto universal para set-top boxes inext com zona de programação. Compatível com os modelos de set-top box – TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2.",
        description:
          "O controle remoto programável inext permite controlar simultaneamente a sua set-top box e a sua TV.",
        keyFeatures: [
          "Zona de controle da TV programável — copie os comandos do controle remoto da sua televisão para simplificar a configuração.",
          "Teclado numérico — troque de canais facilmente, diretamente no seu serviço de IPTV.",
          "Compatível com todos os modelos clássicos da inext — TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2.",
          "Conjunto completo de botões de navegação e controle de reprodução de mídia.",
        ],
        specifications: {
          Tipo: "Controle remoto IR programável universal",
          Compatibilidade: "inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2",
          "Teclas programáveis": "Sim (TV Power, TV Input, Vol+, Vol-)",
          Alimentação: "2 x pilhas AAA (não incluídas)",
          Cor: "Preto",
        },
      },
      es: {
        title: "Control remoto inext V3.0",
        shortDesc:
          "Control remoto universal para Set-Top Boxes inext con zona de programación. Compatible con los modelos de Set-Top Box – TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2.",
        description:
          "El control remoto programable inext permite controlar simultáneamente su Set-Top Box y su TV.",
        keyFeatures: [
          "Zona de control de TV programable — copie los comandos del control remoto de su televisor para simplificar la configuración.",
          "Teclado numérico — cambie de canales fácilmente, directamente en su servicio de IPTV.",
          "Compatible con todos los modelos clásicos de inext — TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2.",
          "Conjunto completo de botones de navegación y control de reproducción de contenido.",
        ],
        specifications: {
          Tipo: "Control remoto IR programable universal",
          Compatibilidad: "inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2",
          "Teclas programables": "Sí (TV Power, TV Input, Vol+, Vol-)",
          Alimentación: "2 x pilas AAA (no incluidas)",
          Color: "Negro",
        },
      },
    },
  },
  {
    id: "remote-v2-1",
    category: "remotes",
    image: remoteV21Img,
    descriptionLink: { accessoryId: "remote-v2", text: "V2.0" },
    en: {
      title: "Remote control inext V2.1",
      shortDesc:
        "Programmable IR remote control with dedicated channel up and channel down buttons (Channel+ / Channel−). Compatible with inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, and 4K2 set-top boxes.",
      description:
        "The inext V2.1 programmable remote control lets users control both their set-top box and TV. Unlike V2.0, it replaces the Previous and Next buttons with dedicated channel up and channel down buttons, marked Channel+ and Channel−.",
      keyFeatures: [
        "Programmable TV Control Zone — learn commands from your television remote control to simplify your setup.",
        "Numeric keypad — easily switch channels directly on your IPTV service.",
        "Dedicated channel up and channel down buttons (Channel+ / Channel−) — the key difference from V2.0, which has Previous and Next buttons in the same position.",
        "Compatible with all classic inext models — TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2.",
        "Full navigation and media playback control buttons.",
      ],
      specifications: {
        Type: "Universal Programmable IR Remote",
        Compatibility: "inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2",
        "Dedicated shortcut buttons":
          "Channel up / channel down (Channel+ / Channel−); V2.0 uses Previous / Next",
        "Programmable Keys": "Yes (TV Power, TV Input, Vol+, Vol-)",
        "Power source": "2 x AAA Batteries (not included)",
        Color: "Black",
      },
    },
    localized: {
      uk: {
        title: "Пульт дистанційного керування inext V2.1",
        shortDesc:
          "Програмований ІЧ-пульт з окремими кнопками переходу на наступний і попередній канал (Channel+ / Channel−). Сумісний із приставками inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3 та 4K2.",
        description:
          "Програмований пульт inext V2.1 дає змогу керувати приставкою та телевізором. На відміну від V2.0, замість кнопок «Назад» і «Далі» він має окремі кнопки переходу на наступний і попередній канал, позначені Channel+ і Channel−.",
        keyFeatures: [
          "Програмована зона керування ТВ — навчіть пульт основним командам вашого телевізора (живлення, гучність, джерело сигналу).",
          "Цифрова клавіатура — зручне перемикання каналів на ТБ та в IPTV-застосунках.",
          "Окремі кнопки наступного й попереднього каналу (Channel+ / Channel−) — ключова відмінність від V2.0, де на їхньому місці розташовані кнопки «Назад» і «Далі».",
          "Сумісність — працює з моделями приставок inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3 та 4K2.",
          "Повний набір кнопок навігації та керування відтворенням мультимедіа.",
        ],
        specifications: {
          Тип: "Універсальний програмований ІЧ-пульт",
          Сумісність: "inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2",
          "Окремі кнопки швидкого доступу":
            "Наступний / попередній канал (Channel+ / Channel−); у V2.0 — «Назад» / «Далі»",
          "Програмовані клавіші": "Так (Ввімкнення ТВ, Вибір джерела, Гучність+, Гучність-)",
          Живлення: "2 x AAA батарейки (не входять до комплекту)",
          Колір: "Чорний",
        },
      },
      pl: {
        title: "Pilot zdalnego sterowania inext V2.1",
        shortDesc:
          "Programowalny pilot IR z osobnymi przyciskami następnego i poprzedniego kanału (Channel+ / Channel−). Jest zgodny z dekoderami inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3 i 4K2.",
        description:
          "Programowalny pilot inext V2.1 umożliwia sterowanie dekoderem i telewizorem. W odróżnieniu od V2.0 zamiast przycisków Poprzedni i Następny ma osobne przyciski następnego i poprzedniego kanału, oznaczone Channel+ i Channel−.",
        keyFeatures: [
          "Programowalna strefa TV — pozwala skopiować najważniejsze funkcje pilota telewizora i obsługiwać oba urządzenia jednym pilotem.",
          "Klawiatura numeryczna — prosta i szybka zmiana kanałów bezpośrednio w serwisie IPTV.",
          "Osobne przyciski następnego i poprzedniego kanału (Channel+ / Channel−) — główna różnica względem V2.0, który w tym samym miejscu ma przyciski Poprzedni i Następny.",
          "Szeroka kompatybilność — działa z modelami inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3 i 4K2.",
          "Wygodny blok przycisków nawigacyjnych i sterowania multimediami.",
        ],
        specifications: {
          Typ: "Uniwersalny programowalny pilot IR",
          Kompatybilność: "inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2",
          "Osobne przyciski skrótów":
            "Następny / poprzedni kanał (Channel+ / Channel−); w V2.0: Poprzedni / Następny",
          "Programowalne klawisze": "tak (zasilanie TV, wybór źródła, głośność +/−)",
          Zasilanie: "2 × baterie AAA (brak w zestawie)",
          Kolor: "czarny",
        },
      },
      pt: {
        title: "Controle remoto inext V2.1",
        shortDesc:
          "Controle remoto IR programável com botões dedicados para o canal seguinte e o canal anterior (Channel+ / Channel−). Compatível com as set-top boxes inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3 e 4K2.",
        description:
          "O controle remoto programável inext V2.1 permite controlar a set-top box e a TV. Diferentemente do V2.0, ele substitui os botões Anterior e Próximo por botões dedicados para o canal seguinte e o canal anterior, identificados como Channel+ e Channel−.",
        keyFeatures: [
          "Zona de controle da TV programável — copie os comandos do controle remoto da sua televisão para simplificar a configuração.",
          "Teclado numérico — troque de canais facilmente, diretamente no seu serviço de IPTV.",
          "Botões dedicados para o canal seguinte e o canal anterior (Channel+ / Channel−) — a principal diferença em relação ao V2.0, que tem os botões Anterior e Próximo na mesma posição.",
          "Compatível com os modelos clássicos da inext — TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3 e 4K2.",
          "Conjunto completo de botões de navegação e controle de reprodução de mídia.",
        ],
        specifications: {
          Tipo: "Controle remoto IR programável universal",
          Compatibilidade: "inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2",
          "Botões de atalho dedicados":
            "Canal seguinte / canal anterior (Channel+ / Channel−); o V2.0 usa Anterior / Próximo",
          "Teclas programáveis": "Sim (TV Power, TV Input, Vol+, Vol-)",
          Alimentação: "2 x pilhas AAA (não incluídas)",
          Cor: "Preto",
        },
      },
      es: {
        title: "Control remoto inext V2.1",
        shortDesc:
          "Control remoto IR programable con botones dedicados para subir y bajar de canal (Channel+ / Channel−). Compatible con las Set-Top Boxes inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3 y 4K2.",
        description:
          "El control remoto programable inext V2.1 permite controlar la Set-Top Box y la TV. A diferencia del V2.0, sustituye los botones Anterior y Siguiente por botones dedicados para subir y bajar de canal, marcados Channel+ y Channel−.",
        keyFeatures: [
          "Zona de control de TV programable — copie los comandos del control remoto de su televisor para simplificar la configuración.",
          "Teclado numérico — cambie de canales fácilmente, directamente en su servicio de IPTV.",
          "Botones dedicados para subir y bajar de canal (Channel+ / Channel−) — la diferencia principal con respecto al V2.0, que tiene los botones Anterior y Siguiente en la misma posición.",
          "Compatible con los modelos clásicos de inext — TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3 y 4K2.",
          "Conjunto completo de botones de navegación y control de reproducción de contenido.",
        ],
        specifications: {
          Tipo: "Control remoto IR programable universal",
          Compatibilidad: "inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2",
          "Botones de acceso directo dedicados":
            "Subir / bajar de canal (Channel+ / Channel−); el V2.0 usa Anterior / Siguiente",
          "Teclas programables": "Sí (TV Power, TV Input, Vol+, Vol-)",
          Alimentación: "2 x pilas AAA (no incluidas)",
          Color: "Negro",
        },
      },
    },
  },
  {
    id: "remote-v2",
    category: "remotes",
    image: remoteV2Img,
    descriptionLink: { accessoryId: "remote-v2-1", text: "V2.1" },
    en: {
      title: "Remote control inext V2.0",
      shortDesc:
        "Programmable IR remote control with dedicated Previous and Next buttons. Compatible with inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, and 4K2 set-top boxes.",
      description:
        "The inext V2.0 programmable remote control lets users control both their set-top box and TV. Unlike V2.1, it has dedicated Previous and Next buttons instead of channel up and channel down buttons (Channel+ / Channel−).",
      keyFeatures: [
        "Programmable TV Control Zone — learn commands from your television remote control to simplify your setup.",
        "Numeric keypad — easily switch channels directly on your IPTV service.",
        "Dedicated Previous and Next buttons — the key difference from V2.1, which has channel up and channel down buttons (Channel+ / Channel−) in the same position.",
        "Compatible with all classic inext models — TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2.",
        "Full navigation and media playback control buttons.",
      ],
      specifications: {
        Type: "Universal Programmable IR Remote",
        Compatibility: "inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2",
        "Dedicated shortcut buttons":
          "Previous / Next; V2.1 uses channel up / channel down (Channel+ / Channel−)",
        "Programmable Keys": "Yes (TV Power, TV Input, Vol+, Vol-)",
        "Power source": "2 x AAA Batteries (not included)",
        Color: "Black",
      },
    },
    localized: {
      uk: {
        title: "Пульт дистанційного керування inext V2.0",
        shortDesc:
          "Програмований ІЧ-пульт зі спеціальними кнопками «Назад» і «Далі». Сумісний із приставками inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3 та 4K2.",
        description:
          "Програмований пульт inext V2.0 дає змогу керувати приставкою та телевізором. На відміну від V2.1, він має окремі кнопки «Назад» і «Далі» замість кнопок наступного й попереднього каналу (Channel+ / Channel−).",
        keyFeatures: [
          "Програмована зона керування ТВ — навчіть пульт основним командам вашого телевізора (живлення, гучність, джерело сигналу).",
          "Цифрова клавіатура — зручне перемикання каналів на ТБ та в IPTV додатках.",
          "Окремі кнопки «Назад» і «Далі» — ключова відмінність від V2.1, де на їхньому місці розташовані кнопки наступного й попереднього каналу (Channel+ / Channel−).",
          "Сумісність — працює з більшістю моделей приставок inext (TV5, TV5 Ultra, TV4, 4K Ultra тощо).",
          "Повний набір кнопок навігації та керування відтворенням мультимедіа.",
        ],
        specifications: {
          Тип: "Універсальний програмований ІЧ-пульт",
          Сумісність: "inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2",
          "Окремі кнопки швидкого доступу":
            "«Назад» / «Далі»; у V2.1 — наступний / попередній канал (Channel+ / Channel−)",
          "Програмовані клавіші": "Так (Ввімкнення ТВ, Вибір джерела, Гучність+, Гучність-)",
          Живлення: "2 x AAA батарейки (не входять до комплекту)",
          Колір: "Чорний",
        },
      },
      pl: {
        title: "Pilot zdalnego sterowania inext V2.0",
        shortDesc:
          "Programowalny pilot IR z osobnymi przyciskami Poprzedni i Następny. Jest zgodny z dekoderami inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3 i 4K2.",
        description:
          "Programowalny pilot inext V2.0 umożliwia sterowanie dekoderem i telewizorem. W odróżnieniu od V2.1 ma osobne przyciski Poprzedni i Następny zamiast przycisków następnego i poprzedniego kanału (Channel+ / Channel−).",
        keyFeatures: [
          "Programowalna strefa TV — pozwala skopiować najważniejsze funkcje pilota telewizora i obsługiwać oba urządzenia jednym pilotem.",
          "Klawiatura numeryczna — prosta i szybka zmiana kanałów bezpośrednio w serwisie IPTV.",
          "Osobne przyciski Poprzedni i Następny — główna różnica względem V2.1, który w tym samym miejscu ma przyciski następnego i poprzedniego kanału (Channel+ / Channel−).",
          "Szeroka kompatybilność — działa ze standardowymi modelami dekoderów inext.",
          "Wygodny blok przycisków nawigacyjnych i sterowania multimediami.",
        ],
        specifications: {
          Typ: "Uniwersalny programowalny pilot IR",
          Kompatybilność: "inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2",
          "Osobne przyciski skrótów":
            "Poprzedni / Następny; w V2.1: następny / poprzedni kanał (Channel+ / Channel−)",
          "Programowalne klawisze": "tak (zasilanie TV, wybór źródła, głośność +/−)",
          Zasilanie: "2 × baterie AAA (brak w zestawie)",
          Kolor: "czarny",
        },
      },
      pt: {
        title: "Controle remoto inext V2.0",
        shortDesc:
          "Controle remoto IR programável com botões dedicados Anterior e Próximo. Compatível com as set-top boxes inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3 e 4K2.",
        description:
          "O controle remoto programável inext V2.0 permite controlar a set-top box e a TV. Diferentemente do V2.1, ele tem botões dedicados Anterior e Próximo em vez dos botões de canal seguinte e canal anterior (Channel+ / Channel−).",
        keyFeatures: [
          "Zona de controle da TV programável — copie os comandos do controle remoto da sua televisão para simplificar a configuração.",
          "Teclado numérico — troque de canais facilmente, diretamente no seu serviço de IPTV.",
          "Botões dedicados Anterior e Próximo — a principal diferença em relação ao V2.1, que tem os botões de canal seguinte e canal anterior (Channel+ / Channel−) na mesma posição.",
          "Compatível com todos os modelos clássicos da inext — TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2.",
          "Conjunto completo de botões de navegação e controle de reprodução de mídia.",
        ],
        specifications: {
          Tipo: "Controle remoto IR programável universal",
          Compatibilidade: "inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2",
          "Botões de atalho dedicados":
            "Anterior / Próximo; o V2.1 usa canal seguinte / canal anterior (Channel+ / Channel−)",
          "Teclas programáveis": "Sim (TV Power, TV Input, Vol+, Vol-)",
          Alimentação: "2 x pilhas AAA (não incluídas)",
          Cor: "Preto",
        },
      },
      es: {
        title: "Control remoto inext V2.0",
        shortDesc:
          "Control remoto IR programable con botones dedicados Anterior y Siguiente. Compatible con las Set-Top Boxes inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3 y 4K2.",
        description:
          "El control remoto programable inext V2.0 permite controlar la Set-Top Box y la TV. A diferencia del V2.1, tiene botones dedicados Anterior y Siguiente en vez de botones para subir y bajar de canal (Channel+ / Channel−).",
        keyFeatures: [
          "Zona de control de TV programable — copie los comandos del control remoto de su televisor para simplificar la configuración.",
          "Teclado numérico — cambie de canales fácilmente, directamente en su servicio de IPTV.",
          "Botones dedicados Anterior y Siguiente — la diferencia principal con respecto al V2.1, que tiene los botones para subir y bajar de canal (Channel+ / Channel−) en la misma posición.",
          "Compatible con todos los modelos clásicos de inext — TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2.",
          "Conjunto completo de botones de navegación y control de reproducción de contenido.",
        ],
        specifications: {
          Tipo: "Control remoto IR programable universal",
          Compatibilidad: "inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2",
          "Botones de acceso directo dedicados":
            "Anterior / Siguiente; el V2.1 usa subir / bajar de canal (Channel+ / Channel−)",
          "Teclas programables": "Sí (TV Power, TV Input, Vol+, Vol-)",
          Alimentación: "2 x pilas AAA (no incluidas)",
          Color: "Negro",
        },
      },
    },
  },
  {
    id: "air-remote",
    category: "remotes",
    image: airRemoteImg,
    en: {
      title: "inext AIR remote control",
      shortDesc:
        "The inext AIR remote control is equipped with a microphone, so you can not only control devices but also use voice input. Use it to control the cursor, type text, and play games. Thanks to the built-in gyroscope, games become more realistic, and the set-top box menu can be navigated with pointer-style control. Works on two AAA batteries.",
      description:
        "The inext AIR remote control features backlit keys, making it even more convenient to use in the dark. The device is made of ABS plastic, which ensures long-term use.",
      keyFeatures: [
        "Compatible with most operating systems — Android, Windows, Smart TV, macOS, etc.",
        "Works on a radio channel at a distance of up to 10 meters, therefore the set-top box can be hidden behind the TV.",
        "The cursor on the screen is controlled by hand movement with the remote control in the air (built-in 6-axis gyroscope).",
        "The built-in microphone allows you to enter text with the help of the voice and give various voice commands.",
        "[POWER] and [SET] buttons support learning function and can be programmed to control a TV or other equipment.",
        "The ergonomic design and buttons' location makes the remote control convenient and protects against accidental presses.",
        "Operates on two AAA batteries (not included).",
        "Backlit keys for comfortable use in the dark.",
        "Dedicated media control buttons.",
        "Adjustable cursor speed for precise control.",
      ],
      specifications: {
        "Connection type": "2.4 GHz USB Receiver",
        Sensor: "6-axis Gyroscope",
        Microphone: "Yes, built-in",
        Backlight: "Yes, blue keys backlight",
        "Programmable Keys": "Yes (Power and Set buttons)",
        "Operating Distance": "Up to 10 meters",
        "Power source": "2 x AAA Batteries (not included)",
        Material: "ABS Plastic",
      },
    },
    localized: {
      uk: {
        title: "Пульт керування inext AIR",
        shortDesc:
          "Пульт inext AIR оснащений мікрофоном, що дозволяє не тільки керувати пристроями, але й здійснювати голосове введення. Пристрій дозволить керувати курсором, вводити текст та грати в ігри. Завдяки гіроскопу ігри стануть реалістичнішими, а керування меню Android приставки відбуватиметься у форматі пульта-указки. Працює від двох батарейок AAA.",
        description:
          "Пульт inext AIR має функцію підсвічування клавіш, що робить його використання в темряві ще зручнішим. Корпус виготовлений з міцного ABS-пластику, що забезпечує тривалу службу.",
        keyFeatures: [
          "Сумісність з більшістю операційних систем — Android, Windows, Smart TV, macOS тощо.",
          "Працює по радіоканалу на відстані до 10 метрів, тому приставку можна сховати за телевізор.",
          "Керування курсором на екрані здійснюється рухом руки з пультом у повітрі (вбудований 6-осьовий гіроскоп).",
          "Вбудований мікрофон дозволяє вводити текст голосом та давати різні голосові команди.",
          "Кнопки [POWER] та [SET] підтримують функцію навчання та можуть бути запрограмовані для керування телевізором або іншою технікою.",
          "Ергономічний дизайн та розташування кнопок роблять пульт зручним та захищають від випадкових натискань.",
          "Працює від двох батарейок AAA 1.5 В (не входять до комплекту).",
          "Підсвічування клавіш для зручності в темряві.",
          "Кнопки швидкого доступу для керування мультимедіа.",
          "Можливість налаштування швидкості руху курсора.",
        ],
        specifications: {
          "Тип підключення": "2.4 ГГц USB-приймач",
          Сенсор: "6-осьовий гіроскоп",
          Мікрофон: "Так, вбудований",
          Підсвічування: "Так (синій колір клавіш)",
          "Програмовані клавіші": "Так (кнопки Power та Set)",
          "Робоча відстань": "До 10 метрів",
          "Джерело живлення": "2 x AAA батарейки (не входять до комплекту)",
          Матеріал: "ABS-пластик",
        },
      },
      pl: {
        title: "Pilot sterowania inext AIR",
        shortDesc:
          "Pilot inext AIR ma wbudowany mikrofon i żyroskop. Umożliwia sterowanie kursorem ruchem dłoni, wprowadzanie tekstu głosem oraz obsługę gier. Działa na dwóch bateriach AAA.",
        description:
          "Podświetlane przyciski ułatwiają korzystanie z pilota inext AIR w ciemności. Obudowa z tworzywa ABS jest lekka i wytrzymała.",
        keyFeatures: [
          "Kompatybilny z większością systemów operacyjnych — Android, Windows, Smart TV, macOS itp.",
          "Łączność radiowa działa w zasięgu do 10 metrów, dlatego dekoder można schować za telewizorem.",
          "Sterowanie kursorem na ekranie odbywa się za pomocą ruchu ręki z pilotem w powietrzu (wbudowany 6-osiowy żyroskop).",
          "Wbudowany mikrofon umożliwia wprowadzanie tekstu głosem i wydawanie różnych poleceń głosowych.",
          "Przyciski [POWER] i [SET] obsługują funkcję uczenia się i można je zaprogramować do sterowania telewizorem lub innymi urządzeniami.",
          "Ergonomiczna konstrukcja i układ przycisków zapewniają wygodną obsługę i ograniczają ryzyko przypadkowych naciśnięć.",
          "Zasilany dwiema bateriami AAA 1,5 V (brak w zestawie).",
          "Podświetlenie przycisków — wygodne korzystanie w ciemności.",
          "Przyciski szybkiego dostępu do sterowania multimediami.",
          "Możliwość precyzyjnego ustawienia prędkości ruchu kursora.",
        ],
        specifications: {
          "Rodzaj połączenia": "odbiornik USB 2,4 GHz",
          Czujnik: "6-osiowy żyroskop",
          Mikrofon: "tak, wbudowany",
          "Podświetlenie klawiszy": "tak, niebieskie podświetlenie",
          "Programowalne klawisze": "tak (przyciski Power i Set)",
          "Zasięg działania": "do 10 m",
          Zasilanie: "2 × baterie AAA (brak w zestawie)",
          Materiał: "tworzywo ABS",
        },
      },
      pt: {
        title: "Controle remoto inext AIR",
        shortDesc:
          "O controle remoto inext AIR é equipado com um microfone, para que você possa não apenas controlar aparelhos, mas também usar a entrada por voz. Use-o para controlar o cursor, digitar texto e jogar. Graças ao giroscópio integrado, os jogos ficam mais realistas, e o menu da set-top box pode ser navegado com controle no estilo apontador. Funciona com duas pilhas AAA.",
        description:
          "O controle remoto inext AIR possui teclas com iluminação, tornando o seu uso ainda mais prático no escuro. O aparelho é fabricado em plástico ABS, o que garante uma longa vida útil.",
        keyFeatures: [
          "Compatível com a maioria dos sistemas operacionais — Android, Windows, Smart TV, macOS, etc.",
          "Funciona por radiofrequência a uma distância de até 10 metros, por isso a set-top box pode ficar escondida atrás da TV.",
          "O cursor na tela é controlado pelo movimento da mão com o controle remoto no ar (giroscópio de 6 eixos integrado).",
          "O microfone integrado permite digitar texto por voz e dar diversos comandos de voz.",
          "Os botões [POWER] e [SET] suportam a função de aprendizado e podem ser programados para controlar uma TV ou outros equipamentos.",
          "O design ergonômico e a disposição dos botões tornam o controle remoto prático e o protegem contra acionamentos acidentais.",
          "Funciona com duas pilhas AAA (não incluídas).",
          "Teclas iluminadas para uso confortável no escuro.",
          "Botões dedicados para controle de mídia.",
          "Velocidade do cursor ajustável para um controle preciso.",
        ],
        specifications: {
          "Tipo de conexão": "Receptor USB 2.4 GHz",
          Sensor: "Giroscópio de 6 eixos",
          Microfone: "Sim, integrado",
          Iluminação: "Sim, teclas com iluminação azul",
          "Teclas programáveis": "Sim (botões Power e Set)",
          "Distância de operação": "Até 10 metros",
          Alimentação: "2 x pilhas AAA (não incluídas)",
          Material: "Plástico ABS",
        },
      },
      es: {
        title: "Control remoto inext AIR",
        shortDesc:
          "El control remoto inext AIR está equipado con un micrófono, para que no solo puedas controlar los dispositivos, sino también usar la entrada por voz. Úsalo para controlar el cursor, escribir texto y jugar. Gracias al giroscopio integrado, los juegos se vuelven más realistas, y el menú de la Set-Top Box se puede navegar con un control tipo apuntador. Funciona con dos pilas AAA.",
        description:
          "El control remoto inext AIR cuenta con teclas retroiluminadas, lo que hace su uso aún más práctico en la oscuridad. El aparato está fabricado en plástico ABS, lo que garantiza una larga vida útil.",
        keyFeatures: [
          "Compatible con la mayoría de los sistemas operativos — Android, Windows, Smart TV, macOS, etc.",
          "Funciona por radiofrecuencia a una distancia de hasta 10 metros, por lo que la Set-Top Box puede quedar oculta detrás de la TV.",
          "El cursor en la pantalla se controla con el movimiento de la mano con el control remoto en el aire (giroscopio de 6 ejes integrado).",
          "El micrófono integrado permite escribir texto por voz y dar diversos comandos de voz.",
          "Los botones [POWER] y [SET] admiten la función de aprendizaje y se pueden programar para controlar una TV u otros equipos.",
          "El diseño ergonómico y la disposición de los botones hacen que el control remoto sea práctico y lo protegen contra pulsaciones accidentales.",
          "Funciona con dos pilas AAA (no incluidas).",
          "Teclas retroiluminadas para un uso cómodo en la oscuridad.",
          "Botones dedicados para el control de contenido.",
          "Velocidad del cursor ajustable para un control preciso.",
        ],
        specifications: {
          "Tipo de conexión": "Receptor USB 2.4 GHz",
          Sensor: "Giroscopio de 6 ejes",
          Micrófono: "Sí, integrado",
          Iluminación: "Sí, teclas con iluminación azul",
          "Teclas programables": "Sí (botones Power y Set)",
          "Distancia de operación": "Hasta 10 metros",
          Alimentación: "2 x pilas AAA (no incluidas)",
          Material: "Plástico ABS",
        },
      },
    },
  },
  {
    id: "hdmi-cable",
    category: "cables",
    image: hdmiCableImg,
    en: {
      title: "HDMI cable 2.0a, 1m, black",
      shortDesc:
        "The HDMI inext cable is a multimedia interface for simultaneous transmission of high-definition digital audio and video (4K at 50/60 Hz). Used to connect a variety of HDMI devices — monitor, TV, computer, laptop, projector, media player (Blu-ray/DVD/HD), PlayStation, etc. The cable provides quality digital signal transmission.",
      description:
        "A premium high-speed HDMI 2.0a cable that delivers excellent signal transmission for TV boxes, gaming systems, and screens. Supports 4K UHD video at 60Hz and advanced HDR metadata.",
      specifications: {
        "Connector Type": "HDMI to HDMI",
        "Form Factor": "Type A",
        "HDMI version": "2.0a",
        Bandwidth: "Up to 18 Gbps (supports 6 Gbps per channel)",
        "Resolution Support": "Supports 4K 3840×2160 resolution @ 50/60 Hz",
        "HDR Support": "Yes, High Dynamic Range",
        "HDMI CEC Support": "Yes, Consumer Electronic Control",
        Length: "1 meter",
        "Jacket Material": "PVC",
        Color: "Black",
      },
    },
    localized: {
      uk: {
        title: "Кабель HDMI 2.0a, 1м, чорний",
        shortDesc:
          "Кабель HDMI inext — це мультимедійний інтерфейс для одночасної передачі цифрового аудіо та відео високої чіткості (4K при 50/60 Гц). Використовується для підключення різних пристроїв з HDMI — монітора, телевізора, комп'ютера, ноутбука, проектора, медіаплеєра (Blu-ray/DVD/HD), PlayStation тощо. Кабель забезпечує якісну передачу цифрового сигналу.",
        description:
          "Високоякісний швидкісний кабель HDMI 2.0a, який гарантує відмінну передачу сигналу для медіаплеєрів, ігрових приставок та екранів. Підтримує роздільну здатність 4K UHD при 60Гц та передачу метаданих HDR.",
        specifications: {
          "Тип роз'єму": "HDMI -> HDMI",
          "Форм-фактор": "Тип A",
          "Версія HDMI": "2.0a",
          "Пропускна здатність": "До 18 Гбіт/с (6 Гбіт/с на канал)",
          "Підтримка роздільної здатності": "4K 3840×2160, 50/60 Гц",
          "Підтримка HDR": "Так, High Dynamic Range",
          "Підтримка HDMI CEC": "Так, Consumer Electronic Control",
          Довжина: "1 метр",
          "Матеріал обплетення": "ПВХ",
          Колір: "Чорний",
        },
      },
      pl: {
        title: "Kabel HDMI 2.0a, 1 m, czarny",
        shortDesc:
          "Kabel HDMI inext przesyła cyfrowy obraz i dźwięk, w tym wideo 4K przy 50/60 Hz. Służy do łączenia telewizorów, monitorów i projektorów z dekoderami, komputerami, konsolami oraz odtwarzaczami multimedialnymi.",
        description:
          "Kabel HDMI 2.0a do odtwarzaczy multimedialnych, konsol i telewizorów. Obsługuje obraz 4K UHD przy 60 Hz oraz metadane HDR.",
        specifications: {
          "Typ złącza": "HDMI do HDMI",
          Format: "Typ A",
          "Wersja HDMI": "2.0a",
          Przepustowość: "do 18 Gb/s (6 Gb/s na kanał)",
          "Obsługiwana rozdzielczość": "4K 3840×2160 przy 50/60 Hz",
          "Obsługa HDR": "tak (High Dynamic Range)",
          "Obsługa HDMI CEC": "tak (Consumer Electronic Control)",
          Długość: "1 m",
          Powłoka: "PCV",
          Kolor: "czarny",
        },
      },
      pt: {
        title: "Cabo HDMI 2.0a, 1 m, preto",
        shortDesc:
          "O cabo HDMI inext é uma interface multimídia para a transmissão simultânea de áudio e vídeo digitais em alta definição (4K a 50/60 Hz). Usado para conectar diversos aparelhos com HDMI — monitor, TV, computador, notebook, projetor, media player (Blu-ray/DVD/HD), PlayStation, etc. O cabo proporciona uma transmissão de sinal digital de qualidade.",
        description:
          "Um cabo HDMI 2.0a de alta velocidade e qualidade premium que oferece excelente transmissão de sinal para set-top boxes, sistemas de jogos e telas. Suporta vídeo 4K UHD a 60 Hz e metadados HDR avançados.",
        specifications: {
          "Tipo de conector": "HDMI para HDMI",
          Formato: "Tipo A",
          "Versão HDMI": "2.0a",
          "Largura de banda": "Até 18 Gbps (suporta 6 Gbps por canal)",
          "Suporte de resolução": "Suporta resolução 4K 3840×2160 a 50/60 Hz",
          "Suporte HDR": "Sim, High Dynamic Range",
          "Suporte HDMI CEC": "Sim, Consumer Electronic Control",
          Comprimento: "1 metro",
          "Material da capa": "PVC",
          Cor: "Preto",
        },
      },
      es: {
        title: "Cable HDMI 2.0a, 1 m, negro",
        shortDesc:
          "El cable HDMI inext es una interfaz multimedia para la transmisión simultánea de audio y video digitales de alta definición (4K a 50/60 Hz). Se utiliza para conectar diversos dispositivos con HDMI — monitor, TV, computadora, laptop, proyector, media player (Blu-ray/DVD/HD), PlayStation, etc. El cable proporciona una transmisión de señal digital de calidad.",
        description:
          "Un cable HDMI 2.0a de alta velocidad y calidad premium que ofrece una excelente transmisión de señal para Set-Top Boxes, sistemas de juegos y pantallas. Admite video 4K UHD a 60 Hz y metadatos HDR avanzados.",
        specifications: {
          "Tipo de conector": "HDMI a HDMI",
          Formato: "Tipo A",
          "Versión HDMI": "2.0a",
          "Ancho de banda": "Hasta 18 Gbps (admite 6 Gbps por canal)",
          "Soporte de resolución": "Admite resolución 4K 3840×2160 a 50/60 Hz",
          "Soporte HDR": "Sí, High Dynamic Range",
          "Soporte HDMI CEC": "Sí, Consumer Electronic Control",
          Longitud: "1 metro",
          "Material de la cubierta": "PVC",
          Color: "Negro",
        },
      },
    },
  },
  {
    id: "remote-standard",
    category: "remotes",
    image: remoteStandardImg,
    en: {
      title: "Remote control inext",
      shortDesc:
        "Universal remote control for inext set-top boxes with a programming zone. Compatible with set-top box models – TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2.",
      description:
        "The inext programmable remote control allows you to simultaneously control your set-top box and TV.",
      keyFeatures: [
        "Programmable TV Control Zone — learn commands from your television remote control to simplify your setup.",
        "Numeric keypad — easily switch channels directly on your IPTV service.",
        "Compatible with all classic inext models — TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2.",
        "Full navigation and media playback control buttons.",
      ],
      specifications: {
        Type: "Universal Programmable IR Remote",
        Compatibility: "inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2",
        "Programmable Keys": "Yes (TV Power, TV Input, Vol+, Vol-)",
        "Power source": "2 x AAA Batteries (not included)",
        Color: "Black",
      },
    },
    localized: {
      uk: {
        title: "Пульт керування inext",
        shortDesc:
          "Універсальний пульт дистанційного керування для приставок inext із зоною програмування. Сумісний з моделями приставок – TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2.",
        description:
          "Програмований пульт inext дозволяє одночасно керувати приставкою та телевізором.",
        keyFeatures: [
          "Програмована зона керування ТВ — навчіть пульт основним командам вашого телевізора (живлення, гучність, джерело сигналу).",
          "Цифрова клавіатура — зручне перемикання каналів на ТБ та в IPTV додатках.",
          "Сумісність — працює з більшістю моделей приставок inext (TV5, TV5 Ultra, TV4, 4K Ultra тощо).",
          "Повний набір кнопок навігації та керування відтворенням мультимедіа.",
        ],
        specifications: {
          Тип: "Універсальний програмований ІЧ-пульт",
          Сумісність: "inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2",
          "Програмовані клавіші": "Так (Ввімкнення ТВ, Вибір джерела, Гучність+, Гучність-)",
          Живлення: "2 x AAA батарейки (не входять до комплекту)",
          Колір: "Чорний",
        },
      },
      pl: {
        title: "Pilot sterowania inext",
        shortDesc:
          "Uniwersalny, programowalny pilot do dekoderów inext. Jest zgodny z modelami TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3 i 4K2.",
        description:
          "Programowalny pilot inext umożliwia jednoczesne sterowanie dekoderem i telewizorem.",
        keyFeatures: [
          "Programowalna strefa TV — pozwala skopiować najważniejsze funkcje pilota telewizora i obsługiwać oba urządzenia jednym pilotem.",
          "Klawiatura numeryczna — prosta i szybka zmiana kanałów bezpośrednio w serwisie IPTV.",
          "Szeroka kompatybilność — działa ze standardowymi modelami dekoderów inext.",
          "Wygodny blok przycisków nawigacyjnych i sterowania multimediami.",
        ],
        specifications: {
          Typ: "Uniwersalny programowalny pilot IR",
          Kompatybilność: "inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2",
          "Programowalne klawisze": "tak (zasilanie TV, wybór źródła, głośność +/−)",
          Zasilanie: "2 × baterie AAA (brak w zestawie)",
          Kolor: "czarny",
        },
      },
      pt: {
        title: "Controle remoto inext",
        shortDesc:
          "Controle remoto universal para set-top boxes inext com zona de programação. Compatível com os modelos de set-top box – TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2.",
        description:
          "O controle remoto programável inext permite controlar simultaneamente a sua set-top box e a sua TV.",
        keyFeatures: [
          "Zona de controle da TV programável — copie os comandos do controle remoto da sua televisão para simplificar a configuração.",
          "Teclado numérico — troque de canais facilmente, diretamente no seu serviço de IPTV.",
          "Compatível com todos os modelos clássicos da inext — TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2.",
          "Conjunto completo de botões de navegação e controle de reprodução de mídia.",
        ],
        specifications: {
          Tipo: "Controle remoto IR programável universal",
          Compatibilidade: "inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2",
          "Teclas programáveis": "Sim (TV Power, TV Input, Vol+, Vol-)",
          Alimentação: "2 x pilhas AAA (não incluídas)",
          Cor: "Preto",
        },
      },
      es: {
        title: "Control remoto inext",
        shortDesc:
          "Control remoto universal para Set-Top Boxes inext con zona de programación. Compatible con los modelos de Set-Top Box – TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2.",
        description:
          "El control remoto programable inext permite controlar simultáneamente su Set-Top Box y su TV.",
        keyFeatures: [
          "Zona de control de TV programable — copie los comandos del control remoto de su televisor para simplificar la configuración.",
          "Teclado numérico — cambie de canales fácilmente, directamente en su servicio de IPTV.",
          "Compatible con todos los modelos clásicos de inext — TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2.",
          "Conjunto completo de botones de navegación y control de reproducción de contenido.",
        ],
        specifications: {
          Tipo: "Control remoto IR programable universal",
          Compatibilidad: "inext TV5, TV5 Ultra, TV4, 4K Ultra, TV2e, TV3, 4K2",
          "Teclas programables": "Sí (TV Power, TV Input, Vol+, Vol-)",
          Alimentación: "2 x pilas AAA (no incluidas)",
          Color: "Negro",
        },
      },
    },
  },
  {
    id: "av-cable",
    category: "cables",
    image: avCableImg,
    en: {
      title: "AV cable inext TV5 and TV5 Ultra",
      shortDesc: "Adapter cable for connecting inext Android set-top boxes to your TV.",
      description:
        "An analog AV cable is designed to connect inext TV5 and TV5 Ultra smart set-top boxes to compatible audio and video equipment.",
      specifications: {
        "Connector type 1": "Composite 3xRCA-jack",
        "Connector type 2": "3.5 mm mini-jack",
        Length: "1 m",
        Color: "Black",
      },
    },
    localized: {
      uk: {
        title: "Кабель AV inext TV5 та TV5 Ultra",
        shortDesc: "Кабель-перехідник для підключення Android приставок inext до телевізора.",
        description:
          "Аналоговий кабель AV розроблений для підключення смарт-приставок inext TV5 та TV5 Ultra до сумісного аудіо- та відеообладнання.",
        specifications: {
          "Тип роз'єму 1": "Композитний 3xRCA-jack",
          "Тип роз'єму 2": "3.5 мм mini-jack",
          Довжина: "1 м",
          Колір: "Чорний",
        },
      },
      pl: {
        title: "Kabel AV inext TV5 oraz TV5 Ultra",
        shortDesc: "Przewód przejściowy do podłączenia dekoderów inext z Androidem do telewizora.",
        description:
          "Analogowy kabel AV służy do podłączenia dekoderów inext TV5 i TV5 Ultra do zgodnych urządzeń audio-wideo.",
        specifications: {
          "Typ złącza 1": "kompozytowe 3 × RCA",
          "Typ złącza 2": "minijack 3,5 mm",
          Długość: "1 m",
          Kolor: "czarny",
        },
      },
      pt: {
        title: "Cabo AV inext TV5 e TV5 Ultra",
        shortDesc: "Cabo adaptador para conectar as set-top boxes Android inext à sua TV.",
        description:
          "Um cabo AV analógico desenvolvido para conectar as set-top boxes inteligentes inext TV5 e TV5 Ultra a equipamentos de áudio e vídeo compatíveis.",
        specifications: {
          "Tipo de conector 1": "Composto 3xRCA-jack",
          "Tipo de conector 2": "3.5 mm mini-jack",
          Comprimento: "1 m",
          Cor: "Preto",
        },
      },
      es: {
        title: "Cable AV inext TV5 y TV5 Ultra",
        shortDesc: "Cable adaptador para conectar las Set-Top Boxes Android inext a su TV.",
        description:
          "Un cable AV analógico diseñado para conectar las Set-Top Boxes inteligentes inext TV5 y TV5 Ultra a equipos de audio y video compatibles.",
        specifications: {
          "Tipo de conector 1": "Compuesto 3xRCA-jack",
          "Tipo de conector 2": "3.5 mm mini-jack",
          Longitud: "1 m",
          Color: "Negro",
        },
      },
    },
  },
  {
    id: "power-supply-tv5",
    category: "power",
    image: powerSupplyTv5Img,
    en: {
      title: "Power supply EB-LD050205, 4*1.7mm",
      shortDesc: "Compatible with inext TV5 and TV5 Ultra models.",
      description:
        "Official 5V 2A power supply for TV set-top boxes inext TV5, TV5 Ultra. Engineered for safe, reliable long-term operation.",
      specifications: {
        Input: "110-240V AC 50/60Hz 0.3A",
        Output: "5V DC 2A",
        "Connector size": "4 * 1.7 mm",
        Compatibility: "inext TV5, TV5 Ultra",
      },
    },
    localized: {
      uk: {
        title: "Блок живлення EB-LD050205, 4*1.7мм",
        shortDesc: "Сумісний з моделями приставок inext TV5 та TV5 Ultra.",
        description:
          "Офіційний блок живлення 5В 2А для телевізійних приставок inext TV5, TV5 Ultra. Забезпечує безпечну та тривалу роботу вашого пристрою.",
        specifications: {
          Вхід: "110-240V AC 50/60Hz 0.3A",
          Вихід: "5V DC 2A",
          "Розмір роз'єму": "4 * 1.7 мм",
          Сумісність: "inext TV5, TV5 Ultra",
        },
      },
      pl: {
        title: "Zasilacz EB-LD050205, 4 × 1,7 mm",
        shortDesc: "Zgodny z dekoderami inext TV5 i TV5 Ultra.",
        description:
          "Oryginalny zasilacz 5 V / 2 A do dekoderów inext TV5 i TV5 Ultra. Zapewnia stabilne i bezpieczne zasilanie.",
        specifications: {
          Wejście: "110–240 V AC, 50/60 Hz, 0,3 A",
          Wyjście: "5 V DC, 2 A",
          "Wymiary złącza": "4 × 1,7 mm",
          Kompatybilność: "inext TV5, TV5 Ultra",
        },
      },
      pt: {
        title: "Fonte de alimentação EB-LD050205, 4*1.7mm",
        shortDesc: "Compatível com os modelos inext TV5 e TV5 Ultra.",
        description:
          "Fonte de alimentação oficial 5V 2A para as set-top boxes de TV inext TV5, TV5 Ultra. Projetada para um funcionamento seguro e confiável por longos períodos.",
        specifications: {
          Entrada: "110-240V AC 50/60Hz 0.3A",
          Saída: "5V DC 2A",
          "Tamanho do conector": "4 * 1.7 mm",
          Compatibilidade: "inext TV5, TV5 Ultra",
        },
      },
      es: {
        title: "Fuente de alimentación EB-LD050205, 4*1.7mm",
        shortDesc: "Compatible con los modelos inext TV5 y TV5 Ultra.",
        description:
          "Fuente de alimentación oficial de 5V 2A para las Set-Top Boxes de TV inext TV5, TV5 Ultra. Diseñada para un funcionamiento seguro y confiable durante largos períodos.",
        specifications: {
          Entrada: "110-240V AC 50/60Hz 0.3A",
          Salida: "5V DC 2A",
          "Tamaño del conector": "4 * 1.7 mm",
          Compatibilidad: "inext TV5, TV5 Ultra",
        },
      },
    },
  },
  {
    id: "power-supply-standard",
    category: "power",
    image: powerSupplyStandardImg,
    en: {
      title: "Power supply EB-LD050205",
      shortDesc: "Compatible with inext models: TV2e, TV3, TV4, 4K2, 4K Ultra.",
      description:
        "Official 5V 2A power supply for classic set-top boxes inext. Engineered for safe, reliable long-term operation.",
      specifications: {
        Input: "110-240V AC 50/60Hz 0.3A",
        Output: "5V DC 2A",
        Compatibility: "inext TV2e, TV3, TV4, 4K2, 4K Ultra",
      },
    },
    localized: {
      uk: {
        title: "Блок живлення EB-LD050205",
        shortDesc: "Підходить для моделей inext: TV2e, TV3, TV4, 4K2, 4K Ultra.",
        description:
          "Офіційний блок живлення 5В 2А для класичних пристроїв inext. Забезпечує стабільну та тривалу експлуатацію.",
        specifications: {
          Вхід: "110-240V AC 50/60Hz 0.3A",
          Вихід: "5V DC 2A",
          Сумісність: "inext TV2e, TV3, TV4, 4K2, 4K Ultra",
        },
      },
      pl: {
        title: "Zasilacz EB-LD050205",
        shortDesc: "Pasuje do modeli inext: TV2e, TV3, TV4, 4K2, 4K Ultra.",
        description:
          "Oryginalny zasilacz 5 V / 2 A do starszych dekoderów inext. Zapewnia stabilne i bezpieczne zasilanie.",
        specifications: {
          Wejście: "110–240 V AC, 50/60 Hz, 0,3 A",
          Wyjście: "5 V DC, 2 A",
          Kompatybilność: "inext TV2e, TV3, TV4, 4K2, 4K Ultra",
        },
      },
      pt: {
        title: "Fonte de alimentação EB-LD050205",
        shortDesc: "Compatível com os modelos inext: TV2e, TV3, TV4, 4K2, 4K Ultra.",
        description:
          "Fonte de alimentação oficial 5V 2A para as set-top boxes clássicas inext. Projetada para um funcionamento seguro e confiável por longos períodos.",
        specifications: {
          Entrada: "110-240V AC 50/60Hz 0.3A",
          Saída: "5V DC 2A",
          Compatibilidade: "inext TV2e, TV3, TV4, 4K2, 4K Ultra",
        },
      },
      es: {
        title: "Fuente de alimentación EB-LD050205",
        shortDesc: "Compatible con los modelos inext: TV2e, TV3, TV4, 4K2, 4K Ultra.",
        description:
          "Fuente de alimentación oficial de 5V 2A para las Set-Top Boxes clásicas inext. Diseñada para un funcionamiento seguro y confiable durante largos períodos.",
        specifications: {
          Entrada: "110-240V AC 50/60Hz 0.3A",
          Salida: "5V DC 2A",
          Compatibilidad: "inext TV2e, TV3, TV4, 4K2, 4K Ultra",
        },
      },
    },
  },
  {
    id: "ir-sensor",
    category: "sensors",
    image: irSensorImg,
    en: {
      title: "IR sensor",
      shortDesc:
        "Compatible with inext TV, TV3, TV4, 4K, 4K Ultra, and TV6 units with a dedicated IR extender port.",
      description:
        "The infrared sensor (IR extender) is designed for flush mounting inext TV, TV3, TV4, 4K, 4K Ultra, and TV6 set-top boxes with a dedicated IR extender port. Some TV6 market variants have an AV port instead; those units are not compatible with this sensor.",
      specifications: {
        "Connector type": "3.5 mm mini-jack",
        "Cable length": "1 m",
        Compatibility: "inext TV, TV3, TV4, 4K, 4K Ultra; TV6 with a dedicated IR extender port",
        "TV6 note":
          "Not compatible with TV6 variants that have an AV port instead of an IR extender port.",
      },
    },
    localized: {
      uk: {
        title: "ІЧ-приймач (подовжувач)",
        shortDesc:
          "Сумісний з inext TV, TV3, TV4, 4K, 4K Ultra та приставками TV6 з окремим портом ІЧ-подовжувача.",
        description:
          "Інфрачервоний датчик (ІЧ-подовжувач) призначений для прихованого монтажу приставок inext TV, TV3, TV4, 4K, 4K Ultra та TV6 з окремим портом ІЧ-подовжувача. Деякі ринкові версії TV6 натомість оснащені AV-портом; такі приставки несумісні з цим датчиком.",
        specifications: {
          "Тип роз'єму": "3.5 мм mini-jack",
          "Довжина кабелю": "1 м",
          Сумісність: "inext TV, TV3, TV4, 4K, 4K Ultra; TV6 з окремим портом ІЧ-подовжувача",
          "Примітка щодо TV6":
            "Несумісний із версіями TV6, у яких замість порту ІЧ-подовжувача встановлено AV-порт.",
        },
      },
      pl: {
        title: "Czujnik IR",
        shortDesc:
          "Kompatybilny z inext TV, TV3, TV4, 4K, 4K Ultra oraz dekoderami TV6 z dedykowanym portem przedłużacza IR.",
        description:
          "Czujnik IR (przedłużacz IR) jest przeznaczony do ukrytego montażu dekoderów inext TV, TV3, TV4, 4K, 4K Ultra oraz TV6 z dedykowanym portem przedłużacza IR. Niektóre warianty rynkowe TV6 są zamiast niego wyposażone w port AV; te urządzenia nie są kompatybilne z tym czujnikiem.",
        specifications: {
          "Typ złącza": "minijack 3,5 mm",
          "Długość kabla": "1 m",
          Kompatybilność:
            "inext TV, TV3, TV4, 4K, 4K Ultra; TV6 z dedykowanym portem przedłużacza IR",
          "Uwaga dot. TV6":
            "Brak kompatybilności z wariantami TV6 wyposażonymi w port AV zamiast portu przedłużacza IR.",
        },
      },
      pt: {
        title: "Sensor IR (extensor)",
        shortDesc:
          "Compatível com as unidades inext TV, TV3, TV4, 4K, 4K Ultra e TV6 com porta dedicada de extensor IR.",
        description:
          "O sensor infravermelho (extensor IR) foi desenvolvido para a instalação embutida das set-top boxes inext TV, TV3, TV4, 4K, 4K Ultra e TV6 com porta dedicada de extensor IR. Algumas variantes de mercado da TV6 possuem uma porta AV no lugar; essas unidades não são compatíveis com este sensor.",
        specifications: {
          "Tipo de conector": "3.5 mm mini-jack",
          "Comprimento do cabo": "1 m",
          Compatibilidade:
            "inext TV, TV3, TV4, 4K, 4K Ultra; TV6 com porta dedicada de extensor IR",
          "Observação sobre a TV6":
            "Não compatível com as variantes da TV6 que possuem uma porta AV no lugar da porta de extensor IR.",
        },
      },
      es: {
        title: "Sensor IR (extensor)",
        shortDesc:
          "Compatible con las unidades inext TV, TV3, TV4, 4K, 4K Ultra y TV6 con puerto dedicado de extensor IR.",
        description:
          "El sensor infrarrojo (extensor IR) está diseñado para la instalación empotrada de las Set-Top Boxes inext TV, TV3, TV4, 4K, 4K Ultra y TV6 con puerto dedicado de extensor IR. Algunas variantes de mercado de la TV6 tienen un puerto AV en su lugar; esas unidades no son compatibles con este sensor.",
        specifications: {
          "Tipo de conector": "3.5 mm mini-jack",
          "Longitud del cable": "1 m",
          Compatibilidad:
            "inext TV, TV3, TV4, 4K, 4K Ultra; TV6 con puerto dedicado de extensor IR",
          "Nota sobre la TV6":
            "No compatible con las variantes de la TV6 que tienen un puerto AV en lugar del puerto de extensor IR.",
        },
      },
    },
  },
];
